/**
 * CP2.2 — useShipToAddressFlow
 *
 * Storefront state controller for the "Ship to My Address" checkout flow.
 * Keeps CP2.1's public rate proxy responses fresh and invalidates them
 * on any change to cart, address, quantity, catalog, or fulfillment mode.
 *
 * Contract:
 *   * `fetchQuotes(address)` calls `POST /api/retail/public/{slug}/shipping/rates`
 *     and stores the returned quote_id + quotes + expiration + package
 *     + cart_hash snapshot.
 *   * The returned `selection` (carrier, service_code, speed_band, etc.) is
 *     what the checkout step 10 will persist on the order draft. It is
 *     wiped whenever the cart or address changes — never silently reused.
 *   * `state.status`: idle | loading | ready | expired | invalidated | error
 *
 * The hook is transport-agnostic (fetch abstraction injected as `apiPost`)
 * to keep it easy to test in isolation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const IDLE = "idle";
const LOADING = "loading";
const READY = "ready";
const EXPIRED = "expired";
const INVALIDATED = "invalidated";
const ERROR = "error";

/**
 * Stable, order-independent cart snapshot. The server produces a
 * deterministic hash; the client can compare against `state.cartHash`
 * to detect drift.
 */
function fingerprintCart(cart) {
  if (!Array.isArray(cart)) return "";
  return cart
    .map((l) => `${l.product_id || l.id}:${Number(l.qty || 0)}`)
    .sort()
    .join("|");
}

// Parcel Shipping Checkout Enforcement — exported so ShipToAddressPanel
// can compare a LIVE, not-yet-submitted address edit against the
// fingerprint of whichever address the current quote was actually
// fetched for, and clear the selected service the instant they
// diverge — not only after the customer resubmits the form.
export function fingerprintAddress(a) {
  if (!a || typeof a !== "object") return "";
  return [a.name, a.street1, a.street2 || "", a.city, a.region, a.postal, a.country]
    .map((s) => String(s || "").trim().toLowerCase())
    .join("|");
}

export function useShipToAddressFlow({
  slug,
  cart,
  fulfillmentMode,
  catalogId,
  sessionId,
  apiPost,           // async (path, body) => data
  now = () => Date.now(),
}) {
  const [state, setState] = useState({
    status: IDLE,
    quoteId: null,
    quotes: [],
    packages: [],
    expiresAt: null,
    errorCode: null,
    errorDetail: null,
    partialProviders: [],
    address: null,
    cartFingerprint: "",
    addressFingerprint: "",
    catalogId: catalogId || null,
    fulfillmentMode: fulfillmentMode || null,
  });

  const [selection, setSelection] = useState(null);
  const abortRef = useRef(null);

  // --- fetch quotes ------------------------------------------------------
  const fetchQuotes = useCallback(
    async (address) => {
      if (!slug) return;
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (_e) {
          /* ignore — controller may already be aborted */
        }
      }
      const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
      abortRef.current = ctrl;
      const cartFp = fingerprintCart(cart);
      const addrFp = fingerprintAddress(address);
      setState((s) => ({
        ...s,
        status: LOADING,
        errorCode: null,
        errorDetail: null,
        address,
        cartFingerprint: cartFp,
        addressFingerprint: addrFp,
        catalogId: catalogId || null,
        fulfillmentMode: fulfillmentMode || null,
      }));
      setSelection(null);
      try {
        const data = await apiPost(
          `/retail/public/${encodeURIComponent(slug)}/shipping/rates`,
          {
            cart: (cart || []).map((l) => ({
              product_id: l.product_id || l.id,
              qty: Number(l.qty || 1),
            })),
            shipping_address: address,
            catalog_id: catalogId || null,
            // Parcel Shipping Checkout Enforcement — binds the persisted
            // quote row to this cart/session so `finalize_shipping_draft`
            // can reject a stale/replayed quote_id from a different
            // session (see order_finalizer.py's session-mismatch check).
            session_id: sessionId || null,
          },
          { signal: ctrl?.signal },
        );
        if (!data?.ok) {
          setState((s) => ({
            ...s,
            status: ERROR,
            errorCode: data?.detail?.code || "rate_quote_failed",
            errorDetail: data?.detail?.detail || null,
          }));
          return;
        }
        setState((s) => ({
          ...s,
          status: READY,
          quoteId: data.quote_id,
          quotes: data.quotes || [],
          packages: data.packages || [],
          expiresAt: data.expires_at,
          partialProviders: Array.isArray(data.errors) ? data.errors : [],
        }));
      } catch (e) {
        if (e?.name === "AbortError") return;
        setState((s) => ({
          ...s,
          status: ERROR,
          errorCode: e?.code || "network_error",
          errorDetail: e?.message || null,
        }));
      }
    },
    [slug, cart, catalogId, fulfillmentMode, sessionId, apiPost],
  );

  // --- Invalidation on external change ----------------------------------
  const currentCartFp = useMemo(() => fingerprintCart(cart), [cart]);

  useEffect(() => {
    if (state.status === IDLE) return;
    // Any of these mutating means the quote is no longer valid.
    const changed =
      state.cartFingerprint !== currentCartFp ||
      state.catalogId !== (catalogId || null) ||
      state.fulfillmentMode !== (fulfillmentMode || null);
    if (changed) {
      setState((s) => ({ ...s, status: INVALIDATED }));
      setSelection(null);
    }
  }, [currentCartFp, catalogId, fulfillmentMode, state.status, state.cartFingerprint, state.catalogId, state.fulfillmentMode]);

  // --- Expiration polling (client-side) ----------------------------------
  useEffect(() => {
    if (state.status !== READY || !state.expiresAt) return undefined;
    const check = () => {
      const exp = Date.parse(state.expiresAt);
      if (!isFinite(exp)) return;
      if (now() >= exp) {
        setState((s) => ({ ...s, status: EXPIRED }));
        setSelection(null);
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [state.status, state.expiresAt, now]);

  // --- Reset when fulfillment mode leaves "shipping" --------------------
  useEffect(() => {
    if (fulfillmentMode !== "shipping" && state.status !== IDLE) {
      setState({
        status: IDLE, quoteId: null, quotes: [], packages: [],
        expiresAt: null, errorCode: null, errorDetail: null,
        partialProviders: [], address: null, cartFingerprint: "",
        addressFingerprint: "", catalogId: null, fulfillmentMode: null,
      });
      setSelection(null);
    }
  }, [fulfillmentMode, state.status]);

  // --- Selection helpers ------------------------------------------------
  const selectQuote = useCallback(
    (q) => {
      if (state.status !== READY || !q) return;
      setSelection({
        quote_id: state.quoteId,
        carrier: q.carrier,
        service_code: q.service_code,
        service_name: q.service_name,
        speed_band: q.speed_band,
        rate_amount: q.rate_amount,
        currency: q.currency,
        estimated_days: q.estimated_days,
        expires_at: state.expiresAt,
        shipping_address: state.address,
        estimated_package: state.packages?.[0] || null,
      });
    },
    [state],
  );

  const clearSelection = useCallback(() => setSelection(null), []);

  return {
    state,
    selection,
    fetchQuotes,
    selectQuote,
    clearSelection,
  };
}
